import { Component, OnInit, ViewChild } from '@angular/core'
import { UntypedFormGroup } from '@angular/forms'
import { StoreService } from '@se-po/se-household-setup-data-access-store'
import { AnalyticsService, UserServiceCsrfTokenService } from '@se-po/shared-data-access-services'
import { DateTime } from 'luxon'
import { SeFeDomainEnvironmentService } from 'se-fe-domain-environment'
import { SeFeFormService } from 'se-fe-form-service'

import { SetupBarService } from '@se-po/shared-ui-setup-bar'
import { MatchFormCreateService } from './match-form-create.service'
import { MatchFormSubmitService } from './match-form-submit.service'

@Component({
  selector: 'se-po-household-setup-match',
  templateUrl: './match.component.html',
  styleUrls: ['./match.component.scss'],
  standalone: false,
})
export class MatchComponent implements OnInit {

  @ViewChild('signInForm') public signInForm: HTMLFormElement

  public form: UntypedFormGroup
  public ready = false
  public actionUrl: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public options: any[]
  public multi = true
  public csrfToken: string

  private readonly app = 'SingleSignOn'
  private readonly page = 'DuplicateLookup'

  constructor(
    public store: StoreService,
    public analyticsService: AnalyticsService,
    private env: SeFeDomainEnvironmentService,
    private formCreator: MatchFormCreateService,
    private formService: SeFeFormService,
    private submitter: MatchFormSubmitService,
    private csrfTokenService: UserServiceCsrfTokenService,
    public setupBarService: SetupBarService,
  ) { }

  public get submitting(): boolean {
    return this.submitter.submitting
  }

  public set submitting(val: boolean) {
    this.submitter.submitting = val
  }

  public ngOnInit(): void {
    if (!this.store.accountValid) return this.reset()

    this.actionUrl = `${this.env.getUrl('userService')}users/sign_in`
    this.createForm()
    this.createOptions()
    this.analyticsService.pageView(this.app, this.page)
    this.csrfTokenService.fetchCsrfToken().subscribe(csrfToken => {
      this.csrfToken = csrfToken.csrf_token
      this.ready = true
    })
    this.setupBarService.setAttributes({
      title: 'Create Account',
      percentComplete: 65
    })
  }

  public reset(): void {
    this.store.reset()
    this.submitting = true
    this.submitter.reset(this.store)
  }

  public submit(): void {
    this.submitting = true
    if (!this.formService.isValid(this.form)) {
      this.submitting = false
      this.analyticsService.trackFormErrors(this.form)
      return
    }
    this.trackSelection()
    this.submitter.signIn(this.signInForm)
  }

  public noMatch(): void {
    this.analyticsService.seEvent('BackToSignUpClick')
    this.submitter.noMatch(this.store)
  }

  public trackSelection(): void {
    const position = this.selectedPosition()
    this.analyticsService.seEvent(`ContinueClick.${position}OptionSelected`)
  }

  public selectedPosition(): string {
    const selectedUserValue = this.form.get('selectedUser').value
    const index = this.options.findIndex(option => option.value === selectedUserValue)
    let position = 'Other'
    if (index === 0) {
      position = 'First'
    } else if (index === 1) {
      position = 'Second'
    }
    return position
  }

  private createForm(): void {
    this.form = this.formCreator.createForm()
  }

  private createOptions(): void {
    this.options = this.store.dupes.user_duplicate_lookup_results
      .slice(0, 5)
      .map(dupe => {
        const lastLogin = DateTime.fromISO(dupe.found_user_last_login)
          .toLocaleString(DateTime.DATE_MED)
        return {
          label: dupe.found_user_obfuscated_email,
          value: dupe.found_user_uuid,
          help: `Last Login: ${lastLogin}`,
        }
      })
    if (this.options.length === 1) {
      this.multi = false
      this.form.patchValue({
        selectedUser: this.options[0].value as string,
      })
    }
  }
}
